import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback } from "react";
import { useAppContext } from "../../AppProvider";
import { ModalConfig } from "../../Models/ModalConfig";
import { useCollectionIndexContext } from "./providers";
import {
  faEye,
  faTrash,
  faCalendarCheck,
} from "../../utilities/fontawesomeIcons";
import { Collection } from "../../Models/Collection";
import { useTypes } from "../../utilities/types";
import { getBadge } from "../../Components/Badges/utilities";
import { UncontrolledTooltip } from "reactstrap";
import moment from "moment";
import { isCollectionCancellable } from "./utilities/index.ts";

/* ===========================================================================
 * COMPONENT: CollectionListItem
 * =========================================================================== */

/**
 * @component CollectionListItem
 *
 * Renders a single row in the collection list.
 * Features:
 * - Responsive layout switching (mobile vs desktop) using CSS Grid.
 * - Dynamic badge generation for collection status.
 * - Quick action buttons (View/Edit, Book, Delete).
 * - Tooltips for carrier and owner details.
 *
 * @param {Object} props
 * @param {React.RefObject} props.listRef - Reference to the parent list for refresh operations.
 * @param {Collection} props.item - The collection entity data for the current row.
 * @param {number} props.index - The row index (used for unique tooltip IDs).
 */
const CollectionListItem: React.FC<{
  listRef: React.RefObject<{
    refresh: () => void;
  } | null>;
  item: Collection;
  index: number;
}> = React.memo(({ item, index }) => {
  const { dictionary, breakpoint } = useAppContext();
  const indexCollectionsContext = useCollectionIndexContext();

  /* ---------------------------------------------------------------------------
   * HELPER FUNCTIONS
   * ------------------------------------------------------------------------- */

  /**
   * Helper to update the global modal configuration and open it.
   */
  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      indexCollectionsContext?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [indexCollectionsContext]
  );

  /* ---------------------------------------------------------------------------
   * LOGIC & STYLING CONFIGURATION
   * ------------------------------------------------------------------------- */

  const { collectionStatusList } = useTypes();

  /** Mapping status to badge data */
  const badge = {
    collectionStatus: collectionStatusList.find((o) => o.value === item.status),
  };

  /** Responsive Check */
  const isSmall = ["xs", "sm"].includes(breakpoint);

  /** Responsive Grid Definition */
  const gridTemplateColumns = isSmall
    ? "0.75fr 1fr 1fr 1fr"
    : "0.5fr 0.5fr 0.5fr 0.75fr 0.75fr 2fr 0.75fr 0.75fr auto";

  /** Responsive Grid Area Assignment */
  const gridTemplateAreas = isSmall
    ? `
      " status owner collectionDate ."
      " carrier carrierReference numPackages totalWeight "
      " pickup pickup pickup pickup "
      " row row row row "
      " action action action action"
    `
    : `
      "status owner carrier carrierReference collectionDate pickup numPackages totalWeight action"
    `;

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <div className="list-item px-2">
      <div
        className="d-grid grid-column align-items-center"
        style={{
          gridTemplateColumns: gridTemplateColumns,
          gridTemplateAreas: gridTemplateAreas,
        }}
      >
        {/* Status Badge */}
        <div style={{ gridArea: "status" }}>
          <div>
            {badge.collectionStatus?.label && (
              <div>
                {getBadge({
                  text: badge.collectionStatus.label,
                  bgColor: badge.collectionStatus.color,
                })}
              </div>
            )}
          </div>
        </div>

        {/* Carrier Logo & Tooltip */}
        <div style={{ gridArea: "carrier" }}>
          <img
            id={"carrier_" + index}
            src={item.carrierConfiguration?.carrierLogo}
            alt="carrier-logo"
          />
          <UncontrolledTooltip placement="right" target={"carrier_" + index}>
            <label className="label-2 text-light">
              {dictionary.registries.company}:
            </label>
            <label className="d-block">
              {item.carrierConfiguration?.carrierName}
            </label>
          </UncontrolledTooltip>
        </div>

        {/* Carrier Reference */}
        <div style={{ gridArea: "carrierReference" }}>
          <label className="label-2">
            {dictionary.references.carrier_reference}
          </label>
          <label className="label-3">{item.carrierReference || "-"}</label>
        </div>

        {/* Collection Date */}
        <div style={{ gridArea: "collectionDate" }}>
          <label className="label-2">{dictionary.dates.collection_date}</label>
          <label className="label-3">
            {item.date ? `${moment(item.date).format("DD/MM/YYYY")}` : "-"}
          </label>
        </div>

        {/* Pickup Location Label */}
        <div style={{ gridArea: "pickup" }}>
          <label className="label-2">{dictionary.registries.pickup}</label>
          <label className="label-3">
            {item.addressBookId ? `${item.addressBook.location.label}` : "-"}
          </label>
        </div>

        {/* Owner/Customer Logo & Tooltip */}
        <div style={{ gridArea: "owner" }}>
          <label className="label-2">{dictionary.registries.customer}</label>
          {item.owner && (
            <>
              <img
                id={"owner_" + index}
                src={item.owner.logoDarkUrl}
                alt="owner-logo"
              />
              <UncontrolledTooltip placement="right" target={"owner_" + index}>
                <label className="label-2 text-light">
                  {dictionary.registries.company}:
                </label>
                <label className="d-block">{item.owner.name || "-"}</label>
                {item.owner.externalReference && (
                  <label className="fs-xs">
                    ({item.owner.externalReference})
                  </label>
                )}
              </UncontrolledTooltip>
            </>
          )}
        </div>

        {/* Package Count */}
        <div style={{ gridArea: "numPackages" }}>
          <label className="label-2">
            {dictionary.parcels.number_of_packages_2}
          </label>
          <label className="label-3">{item.numPackages || "-"}</label>
        </div>

        {/* Total Weight */}
        <div style={{ gridArea: "totalWeight" }}>
          <label className="label-2">
            {dictionary.dimensions.total_weight}
          </label>
          <label className="label-3">
            {item.totalWeight ? `${item.totalWeight.toFixed(2)} kg` : "-"}
          </label>
        </div>

        {/* Visual separator for mobile view */}
        {isSmall && <hr style={{ gridArea: "row" }} className="my-1" />}

        {/* Action Buttons Group */}
        <div className="text-end" style={{ gridArea: "action" }}>
          {/* Details/Edit Action */}
          <button
            title={dictionary.actions.details}
            className="button-1 button-1-light button-1-icon me-1"
            onClick={() => {
              openModal(
                item.status === "DRAFT" ? "edit" : "details",
                [item.id],
                "md",
                "",
                dictionary.actions.details
              );
            }}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>

          {/* Booking Action: Disabled if not in Draft status */}
          <button
            title={dictionary.actions.book}
            className="button-1 button-1-light button-1-icon me-1"
            onClick={() => {
              openModal("book", [item.id], "sm", "", dictionary.actions.book);
            }}
            disabled={!isCollectionCancellable(item.status)}
          >
            <FontAwesomeIcon icon={faCalendarCheck} />
          </button>

          {/* Delete Action: Disabled if not in Draft status */}
          <button
            title={dictionary.actions.delete}
            className="button-1 button-1-danger button-1-icon"
            onClick={() => {
              openModal(
                "delete",
                [item.id],
                "sm",
                "",
                dictionary.actions.delete
              );
            }}
            disabled={!isCollectionCancellable(item.status)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </div>
  );
});

export default CollectionListItem;
